import { Fragment } from "react";

import {
  IconArrowRight,
  IconPlus,
  TableContainer,
  TableRowContainer,
  TableShellTitle,
  TableShellValue,
} from "@frontend/common";

import { getAxisRoleLabel, useAxisRoleQuery } from "@api/axisRole/axisRole";
import EditableTextContainer from "@EditArticleContainer/shared/components/EditableTextContainer/EditableTextContainer";
import { Label } from "@EditArticleContainer/shared/components/EditableTextContainer/types/editableText.types";
import {
  REF_CODE_KEY_RIGHTS_LASC_ASSET,
  REF_CODE_KEY_RIGHTS_LASC_EXCLUSIVITY,
  REF_CODE_KEY_RIGHTS_LASC_INTENDED_USE,
  REF_CODE_KEY_RIGHTS_LASC_RIGHTS_LIMITATION,
  REF_CODE_KEY_RIGHTS_LASC_RIGHTS_SCOPE,
  REF_CODE_KEY_RIGHTS_LASC_TERRITORY,
  REF_CODE_KEY_RIGHTS_LASC_TYPE,
} from "@EditArticleContainer/shared/libs/refCode/constants/refCode.constants";
import { createRefCode } from "@EditArticleContainer/shared/libs/refCode/utils/createRefCode";
import { RightsScopeTableItem } from "@EditArticleContainer/shared/types/paragraphContents/contents/keyRightsLASCContent.types";

import * as S from "./KeyRightsLASCTableGroup.style";
import {
  assetLabels,
  assetText,
  benefitingAxisValues,
  contributingAxisValues,
  exclusivityLabels,
  exclusivityText,
  intendedUseLabels,
  intendedUseText,
  rightsLimitationLabels,
  rightsLimitationText,
  rightsScopeLabels,
  rightsScopeText,
  rightsTypeLabels,
  rightsTypeText,
  territoryLabels,
  territoryText,
} from "../../utils/getKeyRightsLASCShellValue";

interface Props {
  items: { item: RightsScopeTableItem; index: number }[];
}

const VALUE_SHELL_PADDING = "0.2rem";
const EDITABLE_TEXT_STYLE = { padding: "0.8rem", borderRadius: "0" };

const COLUMNS: {
  title: string;
  getText: (item: RightsScopeTableItem) => string;
  getLabels: (item: RightsScopeTableItem) => Label[];
  refBase: string;
  alwaysShow?: boolean;
}[] = [
  {
    title: "Asset(s)",
    getText: assetText,
    getLabels: assetLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_ASSET,
    alwaysShow: true,
  },
  {
    title: "Type",
    getText: rightsTypeText,
    getLabels: rightsTypeLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_TYPE,
  },
  {
    title: "Scope",
    getText: rightsScopeText,
    getLabels: rightsScopeLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_RIGHTS_SCOPE,
  },
  {
    title: "Rights Limitation",
    getText: rightsLimitationText,
    getLabels: rightsLimitationLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_RIGHTS_LIMITATION,
  },
  {
    title: "Intended Use",
    getText: intendedUseText,
    getLabels: intendedUseLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_INTENDED_USE,
    alwaysShow: true,
  },
  {
    title: "Exclusivity",
    getText: exclusivityText,
    getLabels: exclusivityLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_EXCLUSIVITY,
  },
  {
    title: "Territory",
    getText: territoryText,
    getLabels: territoryLabels,
    refBase: REF_CODE_KEY_RIGHTS_LASC_TERRITORY,
  },
];

const toPartnerLabel = (axis: string) => {
  const map: Record<string, string> = {
    A: "Partner A",
    B: "Partner B",
    C: "Partner C",
  };
  return map[axis] ?? `Partner ${axis}`;
};

const KeyRightsLASCTableGroup = ({ items }: Props) => {
  const { data: axisRole } = useAxisRoleQuery();
  const firstItem = items[0].item;
  const contributing = contributingAxisValues(firstItem).filter(Boolean);
  const benefiting = benefitingAxisValues(firstItem).filter(Boolean);

  // Asset / Intended Use를 제외한 컬럼은, 모든 행에서 값이 비어있으면 표출하지 않는다.
  const visibleColumns = COLUMNS.filter(
    (col) =>
      col.alwaysShow ||
      items.some(({ item }) => col.getText(item).trim() !== ""),
  );

  return (
    <S.TableGroupWrapper>
      {(contributing.length > 0 || benefiting.length > 0) && (
        <S.FlowRow>
          {contributing.length > 0 && (
            <S.AxisChipsGroup>
              {contributing.map((axis, index) => {
                const role = getAxisRoleLabel(axisRole, axis);
                return (
                  <Fragment key={axis}>
                    {index > 0 && (
                      <S.AxisChipPlus>
                        <IconPlus width={16} height={16} color="#7c899c" />
                      </S.AxisChipPlus>
                    )}
                    <S.AxisChip>
                      {toPartnerLabel(axis)}
                      {role && <S.AxisRole>({role})</S.AxisRole>}
                    </S.AxisChip>
                  </Fragment>
                );
              })}
            </S.AxisChipsGroup>
          )}
          {contributing.length > 0 && benefiting.length > 0 && (
            <S.ArrowIcon>
              <IconArrowRight width={20} height={20} color="#7c899c" />
            </S.ArrowIcon>
          )}
          {benefiting.length > 0 && (
            <S.AxisChipsGroup>
              {benefiting.map((axis, index) => {
                const role = getAxisRoleLabel(axisRole, axis);
                return (
                  <Fragment key={axis}>
                    {index > 0 && (
                      <S.AxisChipPlus>
                        <IconPlus width={16} height={16} color="#7c899c" />
                      </S.AxisChipPlus>
                    )}
                    <S.AxisChip>
                      {toPartnerLabel(axis)}
                      {role && <S.AxisRole>({role})</S.AxisRole>}
                    </S.AxisChip>
                  </Fragment>
                );
              })}
            </S.AxisChipsGroup>
          )}
        </S.FlowRow>
      )}

      <TableContainer>
        <TableRowContainer>
          {visibleColumns.map((col) => (
            <TableShellTitle key={col.title}>{col.title}</TableShellTitle>
          ))}
        </TableRowContainer>
        {items.map(({ item, index }) => (
          <TableRowContainer key={index}>
            {visibleColumns.map((col) => (
              <TableShellValue key={col.title} padding={VALUE_SHELL_PADDING}>
                <EditableTextContainer
                  item={{
                    text: col.getText(item),
                    labels: col.getLabels(item),
                  }}
                  refKey={createRefCode(col.refBase, index)}
                  canTextEdit
                  style={EDITABLE_TEXT_STYLE}
                />
              </TableShellValue>
            ))}
          </TableRowContainer>
        ))}
      </TableContainer>
    </S.TableGroupWrapper>
  );
};

export default KeyRightsLASCTableGroup;
